import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusLevumiItemGerman } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemGerman.js';
import { LevumiItemFilter } from '@chancenwerk/campus-learning-content/entities/levumi/LevumiItemFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/fonts.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { from } from 'rxjs';
import { CampusDialogEditLevumiGermanItem } from '../components/dialogs/campus-dialog-edit-levumi-german-item.js';

@customElement('campus-page-levumi-german-functions')
export class CampusPageLevumiGermanFunctions extends ChemistryLitElement {
  @state() private readonly _filter = new LevumiItemFilter({});

  @state() private readonly _numberOfItems = new RXController(
    this,
    from(LearningContentAPI.levumi.getNumberOfItems(CampusLevumiItemGerman._class)()),
  );

  @state() size = new WindowSizeController(this);

  override render() {
    return permissionGuard(ChancenPermission.campus.EDIT_CONTENT, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="md">
          <cr-h2> LEVUMI Deutsch </cr-h2>
          <cr-text> Hier kannst du die Fragen des LEVUMI-Test-Deutsch bearbeiten. </cr-text>
          <cr-text> Gesamt Anzahl der Aufgaben: ${this._numberOfItems.value} </cr-text>
        </cr-flex>
        <cr-flex
          ${css({
            position: 'sticky',
            top: '0',
            zIndex: '2',
            background: this.colors.neutral.shade(50),
          })}
          padding="md"
          w100
        >
          <cr-flex
            horizontal
            crossaxis-center
            space="md"
            ${css({
              background: this.colors.neutral.shade(50),
              paddingTop: this.spacing.md,
              paddingBottom: this.spacing.md,
            })}
          >
            <cr-input
              @value-changed=${(e: ValueEvent<Maybe<string>>) => {
                if (this._filter.search !== e.detail.value) this._filter.search = e.detail.value ?? null;
              }}
              placeholder="Suchen..."
              icon="search"
              flex
            ></cr-input>
            <cr-button
              icon="add"
              @click=${() =>
                CampusDialogEditLevumiGermanItem.showPopup({
                  draggable: this.size.mobile || this.size.tablet,
                  modal: true,
                })}
            >
              Aufgabe hinzufügen
            </cr-button>
          </cr-flex>
        </cr-flex>

        ${this._getDataView()}
      </cr-flex>
    `;
  }

  private _getDataView() {
    return html`
      <cr-data-view
        .options=${{
          class: CampusLevumiItemGerman,
          operation: LearningContentAPI.levumi.subscribeGermanByFilter,
          selectionSet: CampusLevumiItemGerman.allFields,
          view: new View({ filter: this._filter }),
          isSortable: true,
          emptyState: () => this._getEmptyState(),
          onlyShowLastTranslation: true,
          customColumns: [
            {
              columnName: 'Level',
              fieldName: 'level',
              getFieldMapping(entityInstance) {
                return entityInstance.level.level;
              },
            },
            {
              columnName: 'Kategorie',
              fieldName: 'category',
              getFieldMapping(entityInstance) {
                return entityInstance.category;
              },
            },
            {
              columnName: 'Aufgabe',
              fieldName: 'sentence',
              getFieldMapping(entityInstance) {
                return entityInstance.sentence;
              },
            },
            {
              customID: 'features',
              renderItem: entityInstance => {
                return html`
                  <cr-button
                    ${css({
                      backgroundColor: 'white',
                      color: '#E28807',
                      boxShadow: 'none',
                      width: '3rem',
                      justifySelf: 'end',
                    })}
                    icon="edit"
                    @click=${async () => {
                      await this._editItem(entityInstance);
                    }}
                  >
                  </cr-button>
                `;
              },
            },
          ],
        } satisfies DataViewOptions<typeof CampusLevumiItemGerman>}
      >
      </cr-data-view>
    `;
  }

  private _getEmptyState() {
    return html` <cr-text>Es wurden noch keine Items hinzugefügt. Hier gibt es nichts zu sehen.</cr-text> `;
  }

  private async _editItem(item: CampusLevumiItemGerman) {
    await CampusDialogEditLevumiGermanItem.showPopup({
      draggable: this.size.mobile || this.size.tablet,

      modal: true,
      props: {
        levumiGermanItem: item,
      },
    });
  }
}
